import clsx from 'clsx';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Select} from '../controls/Select';
import {useSaaSEditor} from '../../saas/contexts/editor-context';
import {getEdgeVoices, generateEdgeTTS} from '../../saas/edge-tts-client';
import {kokoroClient} from '../../saas/kokoro-client';
import type {KokoroStatus} from '../../saas/kokoro-client';
import styles from './TTSModal.module.scss';

// ─── Types ───

type Provider = 'edge' | 'elevenlabs' | 'cartesia' | 'kokoro';

interface Voice {
  name: string;
  friendlyName: string;
  locale: string;
  gender: string;
  previewUrl?: string;
}

export interface TTSModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (url: string, transcript?: string) => void;
}

// ─── Constants ───

const SPEED_OPTIONS = [
  {value: '-25%', text: '0.75x'},
  {value: '+0%', text: '1x'},
  {value: '+25%', text: '1.25x'},
  {value: '+50%', text: '1.5x'},
  {value: '+100%', text: '2x'},
];

const EDGE_ACCENT_LABELS: Record<string, string> = {
  'en-US': 'US',
  'en-GB': 'British',
  'en-AU': 'Australian',
  'en-IN': 'Indian',
  'en-CA': 'Canadian',
  'en-IE': 'Irish',
  'en-ZA': 'S. African',
  'en-KE': 'Kenyan',
  'en-NG': 'Nigerian',
  'en-HK': 'Hong Kong',
  'en-NZ': 'New Zealand',
  'en-PH': 'Filipino',
  'en-SG': 'Singaporean',
  'en-TZ': 'Tanzanian',
};

const EDGE_PRIMARY_ACCENTS = ['en-US', 'en-GB', 'en-AU', 'en-IN'];

const ELEVENLABS_PRIMARY_ACCENTS = [
  'American',
  'British',
  'Australian',
  'Indian',
];

const CARTESIA_PRIMARY_ACCENTS = ['English'];

const KOKORO_PRIMARY_ACCENTS = ['American', 'British'];

const KOKORO_SPEED_OPTIONS = [
  {value: '0.8', text: '0.8x'},
  {value: '1.0', text: '1x'},
  {value: '1.2', text: '1.2x'},
  {value: '1.5', text: '1.5x'},
];

const PREVIEW_TEXT = 'Hello, this is a preview of my voice.';

// ─── Module-level voice caches ───

let cachedEdgeVoices: Voice[] | null = null;
let cachedElevenLabsVoices: Voice[] | null = null;
let cachedCartesiaVoices: Voice[] | null = null;
let cachedKokoroVoices: Voice[] | null = null;

// ─── Helpers ───

function getShortName(voiceName: string, provider: Provider): string {
  if (provider === 'elevenlabs' || provider === 'cartesia' || provider === 'kokoro') return voiceName;
  // Edge friendlyName format: "Aria (Neural) - en-US"
  const idx = voiceName.indexOf(' (');
  return idx > 0 ? voiceName.slice(0, idx) : voiceName;
}

function getAccentLabel(locale: string, provider: Provider): string {
  if (provider === 'elevenlabs' || provider === 'cartesia' || provider === 'kokoro') return locale;
  return EDGE_ACCENT_LABELS[locale] ?? locale;
}

function getPrimaryAccents(provider: Provider): string[] {
  if (provider === 'elevenlabs') return ELEVENLABS_PRIMARY_ACCENTS;
  if (provider === 'cartesia') return CARTESIA_PRIMARY_ACCENTS;
  if (provider === 'kokoro') return KOKORO_PRIMARY_ACCENTS;
  return EDGE_PRIMARY_ACCENTS;
}

function getDefaultVoice(provider: Provider): string {
  if (provider === 'kokoro') return 'af_heart';
  return provider === 'edge' ? 'en-US-AriaNeural' : '';
}

function getDefaultAccent(provider: Provider): string {
  if (provider === 'edge') return 'en-US';
  if (provider === 'cartesia') return 'English';
  if (provider === 'kokoro') return 'American';
  return 'American';
}

function getCache(provider: Provider): Voice[] | null {
  if (provider === 'edge') return cachedEdgeVoices;
  if (provider === 'cartesia') return cachedCartesiaVoices;
  if (provider === 'kokoro') return cachedKokoroVoices;
  return cachedElevenLabsVoices;
}

function setCache(provider: Provider, voices: Voice[]) {
  if (provider === 'edge') {
    cachedEdgeVoices = voices;
  } else if (provider === 'cartesia') {
    cachedCartesiaVoices = voices;
  } else if (provider === 'kokoro') {
    cachedKokoroVoices = voices;
  } else {
    cachedElevenLabsVoices = voices;
  }
}

function providerNeedsApiKey(provider: Provider): boolean {
  return provider === 'elevenlabs' || provider === 'cartesia';
}

function getApiKeyStorageKey(provider: Provider): string {
  if (provider === 'elevenlabs') return 'cascade-tts-apikey';
  if (provider === 'cartesia') return 'cascade-tts-apikey-cartesia';
  return '';
}

function readStorage(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

// ─── Component ───

export function TTSModal({open, onClose, onGenerate}: TTSModalProps) {
  const saas = useSaaSEditor();
  const isSaaS = !!saas;

  const [view, setView] = useState<'form' | 'picker'>('form');
  const [script, setScript] = useState('');

  // Kokoro browser-side model status (SaaS mode only)
  const [kokoroStatus, setKokoroStatus] = useState<KokoroStatus>(
    () => kokoroClient.getStatus(),
  );
  const [kokoroProgress, setKokoroProgress] = useState<{loaded: number; total: number} | null>(null);

  useEffect(() => {
    if (!isSaaS) return;
    const unsubStatus = kokoroClient.onStatus(status => setKokoroStatus(status));
    const unsubProgress = kokoroClient.onProgress((loaded, total) => {
      setKokoroProgress({loaded, total});
    });
    // Sync current status
    setKokoroStatus(kokoroClient.getStatus());
    return () => {
      unsubStatus();
      unsubProgress();
    };
  }, [isSaaS]);

  // Provider + API key (persisted)
  const [provider, setProvider] = useState<Provider>(() => {
    return readStorage('cascade-tts-provider', 'edge') as Provider;
  });
  const [apiKey, setApiKey] = useState(
    () => readStorage(getApiKeyStorageKey(provider), ''),
  );
  const [apiKeyInput, setApiKeyInput] = useState('');
  const apiKeySaved = apiKey.length > 0;

  // SSML mode (Edge TTS only)
  const [ssmlMode, setSsmlMode] = useState(false);

  // Voice state
  const [selectedVoice, setSelectedVoice] = useState(
    () => getDefaultVoice(provider),
  );
  const [selectedRate, setSelectedRate] = useState('+0%');
  const [selectedKokoroSpeed, setSelectedKokoroSpeed] = useState('1.0');
  const [voices, setVoices] = useState<Voice[]>(getCache(provider) ?? []);
  const [voicesLoading, setVoicesLoading] = useState(!getCache(provider));
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccent, setSelectedAccent] = useState(
    () => getDefaultAccent(provider),
  );
  const [showAllAccents, setShowAllAccents] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ─── Provider switching ───

  const switchProvider = useCallback(
    (next: Provider) => {
      if (next === provider) return;
      setProvider(next);
      localStorage.setItem('cascade-tts-provider', next);
      setError(null);
      if (next !== 'edge') setSsmlMode(false);

      // Swap to the new provider's API key
      const nextKey = readStorage(getApiKeyStorageKey(next), '');
      setApiKey(nextKey);
      setApiKeyInput('');

      // Load from cache or mark as loading
      const cached = getCache(next);
      if (cached) {
        setVoices(cached);
        setVoicesLoading(false);
      } else {
        setVoices([]);
        setVoicesLoading(true);
      }

      // Reset voice/accent to provider defaults
      setSelectedVoice(getDefaultVoice(next));
      setSelectedAccent(getDefaultAccent(next));
      setShowAllAccents(false);
    },
    [provider],
  );

  // ─── API key management ───

  const handleSaveApiKey = useCallback(() => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) return;
    setApiKey(trimmed);
    const storageKey = getApiKeyStorageKey(provider);
    if (storageKey) localStorage.setItem(storageKey, trimmed);
    setApiKeyInput('');
    // Clear cache so voices refetch with new key
    if (provider === 'elevenlabs') cachedElevenLabsVoices = null;
    if (provider === 'cartesia') cachedCartesiaVoices = null;
    setVoicesLoading(true);
  }, [apiKeyInput, provider]);

  const handleClearApiKey = useCallback(() => {
    setApiKey('');
    const storageKey = getApiKeyStorageKey(provider);
    if (storageKey) localStorage.removeItem(storageKey);
    if (provider === 'elevenlabs') cachedElevenLabsVoices = null;
    if (provider === 'cartesia') cachedCartesiaVoices = null;
    setVoices([]);
    setSelectedVoice('');
  }, [provider]);

  // ─── Voice fetching ───

  useEffect(() => {
    if (!open) return;

    // SaaS + Edge TTS: use hardcoded voice list (instant)
    if (isSaaS && provider === 'edge') {
      const edgeVoices = getEdgeVoices();
      setCache('edge', edgeVoices);
      setVoices(edgeVoices);
      setVoicesLoading(false);
      return;
    }

    // SaaS + Kokoro: use client-side voices from the Web Worker
    if (isSaaS && provider === 'kokoro') {
      const clientVoices = kokoroClient.getVoices();
      if (clientVoices.length > 0) {
        const mapped: Voice[] = clientVoices.map(v => ({
          name: v.id,
          friendlyName: v.name,
          locale: v.language,
          gender: v.gender,
        }));
        setCache('kokoro', mapped);
        setVoices(mapped);
        setVoicesLoading(false);
      } else if (kokoroStatus === 'loading') {
        setVoicesLoading(true);
      } else {
        setVoicesLoading(false);
      }
      return;
    }

    // Edge: always fetch
    // ElevenLabs / Cartesia: only fetch when API key is saved
    if (providerNeedsApiKey(provider) && !apiKeySaved) {
      setVoices([]);
      setVoicesLoading(false);
      return;
    }

    const cached = getCache(provider);
    if (cached) {
      setVoices(cached);
      setVoicesLoading(false);
      return;
    }

    setVoicesLoading(true);

    const url = providerNeedsApiKey(provider)
      ? `/__cascade/tts/voices?provider=${provider}&apiKey=${encodeURIComponent(apiKey)}`
      : `/__cascade/tts/voices?provider=${provider}&lang=en`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
          return;
        }
        if (Array.isArray(data)) {
          setCache(provider, data);
          setVoices(data);

          // Auto-select first voice if current selection is invalid
          if (data.length > 0) {
            const defaultVoice = getDefaultVoice(provider);
            if (!defaultVoice || !data.find((v: Voice) => v.name === defaultVoice)) {
              setSelectedVoice(data[0].name);
              setSelectedAccent(data[0].locale);
            }
          }
        }
      })
      .catch(() => {
        /* retry next open */
      })
      .finally(() => setVoicesLoading(false));
  }, [open, provider, apiKeySaved, apiKey, isSaaS, kokoroStatus]);

  // Focus textarea when form view opens
  useEffect(() => {
    if (open && view === 'form') {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, view]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (view === 'picker') {
          setView('form');
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, view, onClose]);

  // Cleanup audio on close
  useEffect(() => {
    if (!open) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      setPreviewPlaying(null);
      setView('form');
    }
  }, [open]);

  // ─── Voice grouping ───

  const voicesByAccent = useMemo(() => {
    const groups: Record<string, Voice[]> = {};
    for (const v of voices) {
      if (!groups[v.locale]) groups[v.locale] = [];
      groups[v.locale].push(v);
    }
    return groups;
  }, [voices]);

  const primaryAccentList = getPrimaryAccents(provider);

  const {primaryAccents, overflowAccents} = useMemo(() => {
    const available = Object.keys(voicesByAccent);
    const primary = primaryAccentList.filter(a => available.includes(a));
    const overflow = available
      .filter(a => !primaryAccentList.includes(a))
      .sort();
    return {primaryAccents: primary, overflowAccents: overflow};
  }, [voicesByAccent, primaryAccentList]);

  const filteredVoices = useMemo(
    () => voicesByAccent[selectedAccent] ?? [],
    [voicesByAccent, selectedAccent],
  );

  const currentVoice = useMemo(
    () => voices.find(v => v.name === selectedVoice),
    [voices, selectedVoice],
  );

  // ─── Handlers ───

  const handleGenerate = useCallback(async () => {
    if (!script.trim()) return;
    setIsGenerating(true);
    setError(null);
    try {
      // SaaS + Edge TTS: generate via Cloudflare Worker proxy
      if (isSaaS && provider === 'edge') {
        const blob = await generateEdgeTTS({
          text: ssmlMode ? script.trim() : script.trim(),
          voice: selectedVoice,
          rate: ssmlMode ? undefined : selectedRate,
        });
        const url = URL.createObjectURL(blob);
        onGenerate(url, script.trim());
        return;
      }

      // SaaS + Kokoro: generate in browser via Web Worker
      if (isSaaS && provider === 'kokoro') {
        const blob = await kokoroClient.generate({
          text: script.trim(),
          voice: selectedVoice,
          speed: parseFloat(selectedKokoroSpeed),
        });
        const url = URL.createObjectURL(blob);
        onGenerate(url, script.trim());
        return;
      }

      // Server-side generation (dev mode)
      const body: Record<string, string | boolean | number> = {
        text: script.trim(),
        voice: selectedVoice,
      };
      if (provider === 'edge' && !ssmlMode) {
        body.rate = selectedRate;
      }
      if (provider === 'edge' && ssmlMode) {
        body.ssml = true;
      }
      if (provider === 'elevenlabs') {
        body.provider = 'elevenlabs';
        body.apiKey = apiKey;
      }
      if (provider === 'cartesia') {
        body.provider = 'cartesia';
        body.apiKey = apiKey;
      }
      if (provider === 'kokoro') {
        body.provider = 'kokoro';
        body.speed = parseFloat(selectedKokoroSpeed);
      }
      const res = await fetch('/__cascade/tts', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else if (data.url) {
        // Fire-and-forget: auto-analyze with known transcript for word timing
        fetch('/__cascade/audio/analyze', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            audioPath: data.url,
            transcript: script.trim(),
          }),
        }).catch(() => { /* non-critical */ });
        onGenerate(data.url);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Generation failed.');
    } finally {
      setIsGenerating(false);
    }
  }, [script, selectedVoice, selectedRate, selectedKokoroSpeed, provider, apiKey, ssmlMode, onGenerate, isSaaS]);

  const handlePreview = useCallback(
    async (voiceName: string, e: {stopPropagation: () => void}) => {
      e.stopPropagation();
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (previewPlaying === voiceName) {
        setPreviewPlaying(null);
        return;
      }
      setPreviewPlaying(voiceName);

      // ElevenLabs: use built-in preview_url (free, no credits)
      const voice = voices.find(v => v.name === voiceName);
      if (provider === 'elevenlabs' && voice?.previewUrl) {
        try {
          const audio = new Audio(voice.previewUrl);
          previewAudioRef.current = audio;
          audio.onended = () => {
            setPreviewPlaying(null);
            previewAudioRef.current = null;
          };
          audio.play();
        } catch {
          setPreviewPlaying(null);
        }
        return;
      }

      // SaaS + Edge TTS: generate preview via Supabase Edge Function
      if (isSaaS && provider === 'edge') {
        try {
          const blob = await generateEdgeTTS({
            text: PREVIEW_TEXT,
            voice: voiceName,
          });
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          previewAudioRef.current = audio;
          audio.onended = () => {
            setPreviewPlaying(null);
            previewAudioRef.current = null;
            URL.revokeObjectURL(url);
          };
          audio.play();
        } catch {
          setPreviewPlaying(null);
        }
        return;
      }

      // SaaS + Kokoro: generate preview in browser
      if (isSaaS && provider === 'kokoro') {
        try {
          const blob = await kokoroClient.generate({
            text: PREVIEW_TEXT,
            voice: voiceName,
          });
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          previewAudioRef.current = audio;
          audio.onended = () => {
            setPreviewPlaying(null);
            previewAudioRef.current = null;
            URL.revokeObjectURL(url);
          };
          audio.play();
        } catch {
          setPreviewPlaying(null);
        }
        return;
      }

      // Edge / Cartesia / Kokoro (dev mode): generate preview via server
      const previewBody: Record<string, string> = {
        text: PREVIEW_TEXT,
        voice: voiceName,
      };
      if (provider === 'cartesia') {
        previewBody.provider = 'cartesia';
        previewBody.apiKey = apiKey;
      }
      if (provider === 'kokoro') {
        previewBody.provider = 'kokoro';
      }
      try {
        const res = await fetch('/__cascade/tts', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(previewBody),
        });
        const data = await res.json();
        if (data.url) {
          const audio = new Audio(data.url);
          previewAudioRef.current = audio;
          audio.onended = () => {
            setPreviewPlaying(null);
            previewAudioRef.current = null;
          };
          audio.play();
        }
      } catch {
        setPreviewPlaying(null);
      }
    },
    [previewPlaying, provider, voices, isSaaS],
  );

  const handleOpenPicker = useCallback(() => {
    if (currentVoice) {
      setSelectedAccent(currentVoice.locale);
    }
    setView('picker');
  }, [currentVoice]);

  const handleSelectVoice = useCallback((voiceName: string) => {
    setSelectedVoice(voiceName);
    setView('form');
  }, []);

  if (!open) return null;

  // ─── Derived display values ───

  const voiceDisplayText = voicesLoading
    ? 'Loading...'
    : currentVoice
      ? `${getShortName(currentVoice.friendlyName, provider)} · ${getAccentLabel(currentVoice.locale, provider)}`
      : providerNeedsApiKey(provider) && !apiKeySaved
        ? 'Enter API key first'
        : 'Select voice';

  const canGenerate =
    !isGenerating &&
    script.trim().length > 0 &&
    !voicesLoading &&
    selectedVoice.length > 0 &&
    (!providerNeedsApiKey(provider) || apiKeySaved) &&
    !(isSaaS && provider === 'kokoro' && kokoroStatus !== 'ready');

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {view === 'form' ? (
          <>
            <div className={styles.header}>
              <span className={styles.headerTitle}>Generate Voiceover</span>
              <button className={styles.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.body}>
              {/* Provider pills */}
              <div className={styles.providerRow}>
                <button
                  className={clsx(
                    styles.providerPill,
                    provider === 'edge' && styles.providerPillActive,
                  )}
                  onClick={() => switchProvider('edge')}
                >
                  Edge TTS
                </button>
                <button
                  className={clsx(
                    styles.providerPill,
                    provider === 'elevenlabs' && styles.providerPillActive,
                  )}
                  onClick={() => switchProvider('elevenlabs')}
                >
                  ElevenLabs
                </button>
                <button
                  className={clsx(
                    styles.providerPill,
                    provider === 'cartesia' && styles.providerPillActive,
                  )}
                  onClick={() => switchProvider('cartesia')}
                >
                  Cartesia
                </button>
                <button
                  className={clsx(
                    styles.providerPill,
                    provider === 'kokoro' && styles.providerPillActive,
                  )}
                  onClick={() => switchProvider('kokoro')}
                >
                  Kokoro
                </button>
              </div>

              {/* Kokoro model loading indicator (SaaS only) */}
              {isSaaS && provider === 'kokoro' && kokoroStatus === 'loading' && (
                <div style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <div style={{
                    width: 12, height: 12,
                    border: '2px solid rgba(255,255,255,0.15)',
                    borderTopColor: 'var(--theme)',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  {kokoroProgress
                    ? `Downloading model... ${Math.round((kokoroProgress.loaded / kokoroProgress.total) * 100)}%`
                    : 'Initializing Kokoro TTS...'}
                </div>
              )}
              {isSaaS && provider === 'kokoro' && kokoroStatus === 'error' && (
                <div style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  color: '#fca5a5',
                }}>
                  Failed to load Kokoro model. Try refreshing the page.
                </div>
              )}

              {/* API key section (ElevenLabs / Cartesia) */}
              {providerNeedsApiKey(provider) && (
                <div className={styles.apiKeySection}>
                  {apiKeySaved ? (
                    <div className={styles.apiKeyConfigured}>
                      <span className={styles.apiKeyConfiguredText}>
                        API key configured
                      </span>
                      <button
                        className={styles.apiKeyClearButton}
                        onClick={handleClearApiKey}
                      >
                        Clear
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className={styles.apiKeyRow}>
                        <input
                          type="password"
                          className={styles.apiKeyInput}
                          placeholder="Enter your API key..."
                          value={apiKeyInput}
                          onChange={e =>
                            setApiKeyInput(
                              (e.target as HTMLInputElement).value,
                            )
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveApiKey();
                          }}
                        />
                        <button
                          className={clsx(
                            styles.button,
                            styles.buttonPrimary,
                          )}
                          onClick={handleSaveApiKey}
                          disabled={!apiKeyInput.trim()}
                        >
                          Save
                        </button>
                      </div>
                      <span className={styles.apiKeyHint}>
                        Get one free at{' '}
                        <a
                          href={provider === 'cartesia' ? 'https://cartesia.ai' : 'https://elevenlabs.io'}
                          target="_blank"
                          rel="noopener"
                        >
                          {provider === 'cartesia' ? 'cartesia.ai' : 'elevenlabs.io'}
                        </a>
                      </span>
                    </>
                  )}
                </div>
              )}

              {provider === 'edge' && (
                <div className={styles.textareaHeader}>
                  <span className={styles.settingsLabel}>Script</span>
                  <button
                    className={clsx(
                      styles.ssmlToggle,
                      ssmlMode && styles.ssmlToggleActive,
                    )}
                    onClick={() => setSsmlMode(m => !m)}
                  >
                    SSML
                  </button>
                </div>
              )}
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                placeholder={
                  ssmlMode
                    ? '<prosody rate="-10%">\n  Slow and deliberate intro.\n</prosody>\n<prosody rate="+20%" pitch="+5%">\n  Then pick up the pace!\n</prosody>'
                    : provider === 'cartesia'
                      ? '<speed ratio="0.9">Slow intro.</speed>\n<emotion value="excited">Pick up the energy!</emotion>'
                      : provider === 'kokoro'
                        ? isSaaS && kokoroStatus === 'loading'
                          ? 'Model loading... Type your script while it downloads.'
                          : isSaaS && kokoroStatus === 'error'
                            ? 'Model failed to load. Try refreshing the page.'
                            : 'Enter text... Kokoro runs locally, no API key needed.'
                        : 'Enter voiceover script...'
                }
                value={script}
                rows={4}
                onChange={e =>
                  setScript((e.target as HTMLTextAreaElement).value)
                }
                style={ssmlMode || provider === 'cartesia' ? {fontFamily: 'var(--font-family-mono)', fontSize: '12px'} : undefined}
              />
              <div className={styles.settingsRow}>
                <div className={styles.settingsGroup} style={{flex: 1}}>
                  <span className={styles.settingsLabel}>Voice</span>
                  <div
                    className={styles.voiceField}
                    onClick={
                      providerNeedsApiKey(provider) && !apiKeySaved
                        ? undefined
                        : handleOpenPicker
                    }
                    style={
                      providerNeedsApiKey(provider) && !apiKeySaved
                        ? {opacity: 0.4, cursor: 'default'}
                        : undefined
                    }
                  >
                    <span className={styles.voiceFieldText}>
                      {voiceDisplayText}
                    </span>
                    <ChevronIcon />
                  </div>
                </div>
                {provider === 'edge' && !ssmlMode && (
                  <div className={styles.settingsGroup}>
                    <span className={styles.settingsLabel}>Speed</span>
                    <Select
                      options={SPEED_OPTIONS}
                      value={selectedRate}
                      onChange={setSelectedRate}
                    />
                  </div>
                )}
                {provider === 'kokoro' && (
                  <div className={styles.settingsGroup}>
                    <span className={styles.settingsLabel}>Speed</span>
                    <Select
                      options={KOKORO_SPEED_OPTIONS}
                      value={selectedKokoroSpeed}
                      onChange={setSelectedKokoroSpeed}
                    />
                  </div>
                )}
              </div>
              {error && <span className={styles.error}>{error}</span>}
              <div className={styles.actions}>
                <button
                  className={clsx(styles.button, styles.buttonSecondary)}
                  onClick={onClose}
                  disabled={isGenerating}
                >
                  Cancel
                </button>
                <button
                  className={clsx(styles.button, styles.buttonPrimary)}
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <span className={styles.headerTitle}>
                <button
                  className={styles.backButton}
                  onClick={() => setView('form')}
                >
                  <BackIcon />
                </button>
                Choose a voice
              </span>
              <button className={styles.closeButton} onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.body}>
              <div className={styles.accentSection}>
                <span className={styles.settingsLabel}>Accent</span>
                <div className={styles.accentPills}>
                  {primaryAccents.map(accent => (
                    <button
                      key={accent}
                      className={clsx(
                        styles.accentPill,
                        selectedAccent === accent && styles.accentPillActive,
                      )}
                      onClick={() => setSelectedAccent(accent)}
                    >
                      {getAccentLabel(accent, provider)}
                    </button>
                  ))}
                  {overflowAccents.length > 0 && !showAllAccents && (
                    <button
                      className={styles.accentPill}
                      onClick={() => setShowAllAccents(true)}
                    >
                      &middot;&middot;&middot;
                    </button>
                  )}
                  {showAllAccents &&
                    overflowAccents.map(accent => (
                      <button
                        key={accent}
                        className={clsx(
                          styles.accentPill,
                          selectedAccent === accent &&
                            styles.accentPillActive,
                        )}
                        onClick={() => setSelectedAccent(accent)}
                      >
                        {getAccentLabel(accent, provider)}
                      </button>
                    ))}
                </div>
              </div>
              <div className={styles.voiceList}>
                {filteredVoices.length === 0 && (
                  <div className={styles.voiceRow}>
                    <div className={styles.voiceInfo}>
                      <div className={styles.voiceDesc}>
                        No voices available for this accent
                      </div>
                    </div>
                  </div>
                )}
                {filteredVoices.map(voice => (
                  <div
                    key={voice.name}
                    className={clsx(
                      styles.voiceRow,
                      voice.name === selectedVoice && styles.voiceRowActive,
                    )}
                    onClick={() => handleSelectVoice(voice.name)}
                  >
                    <button
                      className={clsx(
                        styles.previewButton,
                        previewPlaying === voice.name &&
                          styles.previewButtonPlaying,
                      )}
                      onClick={e => handlePreview(voice.name, e)}
                    >
                      {previewPlaying === voice.name ? (
                        <StopIcon />
                      ) : (
                        <PlayIcon />
                      )}
                    </button>
                    <div className={styles.voiceInfo}>
                      <div className={styles.voiceName}>
                        {getShortName(voice.friendlyName, provider)}
                      </div>
                      <div className={styles.voiceDesc}>{voice.gender}</div>
                    </div>
                    <span
                      className={clsx(
                        styles.voiceCheck,
                        voice.name === selectedVoice &&
                          styles.voiceCheckVisible,
                      )}
                    >
                      <CheckIcon />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Icons ───

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      className={styles.voiceFieldChevron}
    >
      <path
        d="M5 4L9 7L5 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 1.5L10 6L3 10.5V1.5Z" fill="currentColor" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <rect x="1" y="1" width="8" height="8" rx="1" fill="currentColor" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 8L6.5 11.5L13 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
